import React, { useState, lazy, Suspense, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  Home, 
  RefreshCw,
  Plus, 
  Building2, 
  Edit3, 
  Menu, 
  X,
  LogOut,
  IndianRupee,
  CreditCard,
  Tag,
  FileText,
  CalendarDays,
  Settings,
  BookOpen,
  Info,
  Bell
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import api from '../utils/api';
// Lazy load ChatWidget to improve initial load time
const ChatWidget = lazy(() => import('./ChatWidget'));

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { isSubscriptionValid } = useSubscription();
  const location = useLocation();
  const isAuthenticated = Boolean(user?.id);

  const inboxQuery = useQuery(
    ['layout-notification-inbox', user?.id],
    async () => {
      const res = await api.get('/notifications/inbox', { params: { limit: 50 } });
      return res.data?.notifications || [];
    },
    {
      enabled: isAuthenticated,
      refetchInterval: 60000,
      staleTime: 30000,
    }
  );
  const unreadCount = (inboxQuery.data || []).filter((n) => !n?.isRead).length;

  // Memoize navigation items to avoid recreating the array on every render
  const navigation = useMemo(
    () => [
      { name: 'Dashboard', href: '/dashboard', icon: Home },
      { name: 'Add/View Visit', href: '/visits', icon: Plus },
      { name: 'Add Hospital', href: '/hospitals/add', icon: Building2 },
      { name: 'Manage/View Categories', href: '/categories', icon: Tag },
      { name: 'Update/View Hospital', href: '/hospitals', icon: Edit3 },
      { name: 'Generate Reports', href: '/reports', icon: FileText },
      { name: 'Payment Dues', href: '/payment-dues', icon: IndianRupee },
      { name: 'Monthly Dues', href: '/payment-dues/monthly', icon: CalendarDays },
      { name: 'Subscription', href: '/subscription', icon: CreditCard },
      { name: 'Notifications', href: '/notifications', icon: Bell },
      { name: 'Profile Settings', href: '/profile', icon: Settings },
      { name: 'Help & Documentation', href: '/help', icon: BookOpen },
      { name: 'About Us', href: '/about', icon: Info },
    ],
    []
  );

  // Stable active-route check so Link components can memoize better
  const isActive = useCallback(
    (href) => location.pathname === href,
    [location.pathname]
  );

  const handleManualRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}

      {/* Sidebar */}
      {/* lg:static was stacking main below a short sidebar; stay fixed on lg so pl-64 aligns with full-height rail */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <img 
              src="/logo.jpg" 
              alt="DrPay Logo" 
              className="h-10 w-40 object-contain"
            />
            {/*<h1 className="text-xl font-bold text-gray-900">DrPay</h1>*/}
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation and User Section Container */}
        <div className="flex flex-col h-full">
          {/* Navigation Section */}
          <nav className="px-3 pt-6 pb-0">
            <div className="space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive(item.href)
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    <span className="flex-1">{item.name}</span>
                    {item.href === '/notifications' && unreadCount > 0 && (
                      <span className="ml-2 inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* User info and logout - Fixed positioning with safe area support */}
          <div className="p-4 border-t border-gray-200 bg-white mt-0" style={{ 
            paddingBottom: `calc(1rem + env(safe-area-inset-bottom, 0px))`
          }}>
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              <LogOut className="mr-3 h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <div 
          className="lg:hidden flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4 bg-white border-b border-gray-200"
          style={{
            paddingTop: `calc(0.75rem + env(safe-area-inset-top, 0px))`,
            minHeight: `calc(3.5rem + env(safe-area-inset-top, 0px))`
          }}
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-500 hover:text-gray-600"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center">
            <img 
              src="/logo.jpg" 
              alt="DrPay Logo" 
              className="h-10 w-40 object-contain mr-0"
            />
            {/*<h1 className="text-base sm:text-lg font-semibold text-gray-900">DrPay</h1>*/}
            <p className="text-xs sm:text-sm text-gray-400 -ml-7">Multi-Hospital Revenue Tracking</p>
          </div>
          {location.pathname !== '/dashboard' ? (
            <div className="flex items-center flex-shrink-0">
              <Link
                to="/notifications"
                className="text-gray-500 hover:text-gray-600 relative mr-2"
                title="Notifications"
              >
                <Bell className="h-6 w-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 inline-flex min-w-[1rem] h-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
              <Link
                to="/dashboard"
                className="text-gray-500 hover:text-gray-600"
                title="Go to Dashboard"
              >
                <Home className="h-6 w-6" />
              </Link>
              <button
                onClick={handleManualRefresh}
                className="text-gray-500 hover:text-gray-600 ml-2"
                title="Refresh page"
              >
                <RefreshCw className="h-6 w-6" />
              </button>
            </div>
          ) : (
            <div className="w-[4.5rem] flex-shrink-0" aria-hidden />
          )}
        </div>

        {/* Desktop: Refresh + Home (hidden on dashboard — same row lives in Dashboard page) */}
        {location.pathname !== '/dashboard' && (
          <div className="hidden lg:flex items-center justify-end pr-6 pt-4 pb-2">
            <Link
              to="/notifications"
              className="btn btn-outline btn-sm mr-2 relative"
              title="Notifications"
            >
              <Bell className="h-4 w-4 mr-2" />
              Notifications
              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 inline-flex min-w-[1.1rem] h-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
            <button
              onClick={handleManualRefresh}
              className="btn btn-outline btn-sm mr-2"
              title="Refresh page"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
            <Link
              to="/dashboard"
              className="btn btn-outline btn-sm"
              title="Go to Dashboard"
            >
              <Home className="h-4 w-4 mr-2" />
              Home
            </Link>
          </div>
        )}

        {/* Page content with safe area bottom padding */}
        <main className="flex-1 p-3 sm:p-4 md:p-6" style={{ 
          paddingBottom: `calc(1.5rem + env(safe-area-inset-bottom, 0px))`
        }}>
          {children}
        </main>
        {isSubscriptionValid && (
          <Suspense fallback={null}>
            <ChatWidget />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default Layout;
